import { z } from "zod";

import { completedYearsBetween, parseIsoCivilDate } from "@/lib/civil-date";
import { isValidCpf } from "@/lib/cpf";
import { onlyDigits as cpfOnlyDigits } from "@/lib/strings";
import { getTodayInBrazil } from "@/lib/dates";

// ─── Constants ───────────────────────────────────────────────────────────────

export const GENDERS = ["MALE", "FEMALE", "NON_BINARY", "UNDISCLOSED"] as const;
export type Gender = (typeof GENDERS)[number];

export const GENDER_LABELS: Record<Gender, string> = {
  MALE: "Masculino",
  FEMALE: "Feminino",
  NON_BINARY: "Outro",
  UNDISCLOSED: "Prefiro não informar",
};

export const ADDRESS_TYPES = ["RESIDENTIAL", "COMMERCIAL"] as const;
export type AddressType = (typeof ADDRESS_TYPES)[number];

export const ADDRESS_TYPE_LABELS: Record<AddressType, string> = {
  RESIDENTIAL: "Residencial",
  COMMERCIAL: "Comercial",
};

// ─── Shared field schemas ────────────────────────────────────────────────────

/**
 * Validates a birthDate string (yyyy-MM-dd):
 * - Must be a real calendar date
 * - Between 1900 and today
 * - Person must be at least 18 years old
 */
export const birthDateSchema = z
  .string()
  .min(1, "Informe a sua data de nascimento.")
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Formato inválido (use aaaa-mm-dd).")
  // Calendar arithmetic comes from `@/lib/civil-date`: none of these rules builds a `Date`
  // from text, because midnight UTC comes back as the previous day in any timezone west of it.
  .refine((value) => parseIsoCivilDate(value) !== null, "Essa data não existe.")
  .refine((value) => {
    const parts = parseIsoCivilDate(value);
    return parts === null || parts.year >= 1900;
  }, "Ano inválido.")
  .refine((value) => value <= getTodayInBrazil(), "A data não pode ser no futuro.")
  .refine((value) => {
    const birth = parseIsoCivilDate(value);
    const today = parseIsoCivilDate(getTodayInBrazil());
    if (birth === null || today === null) return false;

    return completedYearsBetween(birth, today) >= 18;
  }, "Você precisa ter pelo menos 18 anos.");

// ─── CPF step ────────────────────────────────────────────────────────────────

export const cpfStepSchema = z.object({
  cpf: z
    .string()
    .transform(cpfOnlyDigits)
    .pipe(
      z.string().length(11, "Informe os onze dígitos do seu CPF.")
        .refine(isValidCpf, "Esse CPF não confere. Confira os números e tente de novo."),
    ),
});
export type CpfStepValues = z.infer<typeof cpfStepSchema>;

// ─── Terms step ──────────────────────────────────────────────────────────────

export const termsStepSchema = z.object({
  accepted: z.literal(true, "Você precisa aceitar os termos."),
});
export type TermsStepValues = z.infer<typeof termsStepSchema>;

// ─── Address step ────────────────────────────────────────────────────────────

export const addressStepSchema = z
  .object({
    type: z.enum(ADDRESS_TYPES),
    zipCode: z
      .string()
      .transform((v) => v.replace(/\D/g, ""))
      .pipe(z.string().length(8, "Informe um CEP válido.")),
    street: z.string().trim().min(1, "Informe o endereço."),
    number: z.string().trim().min(1, "Informe o número."),
    complement: z.string().trim().optional().or(z.literal("")),
    neighborhood: z.string().trim().min(1, "Informe o bairro."),
    city: z.string().trim().min(1, "Informe a cidade."),
    state: z.string().trim().min(1, "Informe o estado."),
    country: z.string().trim().min(1, "Informe o país."),
    useForBilling: z.boolean(),
    useForDelivery: z.boolean(),
  })
  .refine((data) => data.useForBilling || data.useForDelivery, {
    message: "Selecione ao menos um uso para este endereço.",
    path: ["useForBilling"],
  });
export type AddressStepValues = z.infer<typeof addressStepSchema>;

// ─── Signup (identification) step ────────────────────────────────────────────

export const onboardingSignupSchema = z.object({
  firstName: z.string().trim().min(2, "Informe o seu nome."),
  lastName: z.string().trim().min(2, "Informe o seu sobrenome."),
  phone: z
    .string()
    .transform((v) => v.replace(/\D/g, ""))
    .pipe(z.string().min(10, "Informe um celular com DDD.").max(11, "Celular pode ter no máximo 11 dígitos.")),
  birthDate: birthDateSchema,
  gender: z.enum(GENDERS, "Selecione uma opção."),
  marketingOptIn: z.boolean(),
  email: z.string().email("E-mail inválido."),
});
export type OnboardingSignupValues = z.infer<typeof onboardingSignupSchema>;

// ─── Extra profile fields (subset reused for returning REGISTERED users) ─────

export const extraProfileFieldsSchema = z.object({
  birthDate: birthDateSchema,
  gender: z.enum(GENDERS, "Selecione um gênero."),
  marketingOptIn: z.boolean(),
});
export type ExtraProfileFieldsValues = z.infer<typeof extraProfileFieldsSchema>;
